import { UniversalCrawlerService } from '@/services/universalCrawlerService';
import { Page, CrawlResult, CrawlProgress } from '@/types/crawler';
import { toast } from 'sonner';

export interface SimpleCrawlProgress {
  stage: string;
  progress: number;
  message?: string;
  pagesProcessed?: number;
  totalPages?: number;
}

/**
 * Simple, reliable crawler service that uses only the Edge Function
 * No complex abstraction layers - just direct API calls
 */
export class SimpleCrawlerService {
  private progressCallback: ((progress: SimpleCrawlProgress) => void) | null = null;
  private isCancelled = false;

  registerProgressCallback(callback: (progress: SimpleCrawlProgress) => void) {
    this.progressCallback = callback;
  }

  cancelCrawl() {
    this.isCancelled = true;
  }

  private reportProgress(stage: string, progress: number, message?: string, pagesProcessed: number = 0, totalPages: number = 0) {
    if (this.progressCallback) {
      this.progressCallback({ 
        stage, 
        progress, 
        message,
        pagesProcessed,
        totalPages
      });
    }
  }

  async crawlWebsite(url: string): Promise<CrawlResult> {
    this.isCancelled = false;
    const startTime = Date.now();
    
    try {
      const baseUrl = new URL(url);
      
      this.reportProgress('Fetching sitemap...', 10, 'Looking for sitemap URLs', 0, 0);
      
      // Get sitemap URLs using Edge Function
      const sitemapResult = await UniversalCrawlerService.fetchSitemap(url);
      
      if (!sitemapResult.success) {
        toast.error(`Failed to fetch sitemap: ${sitemapResult.error}`);
        throw new Error(`Sitemap fetch failed: ${sitemapResult.error}`);
      }

      const sitemapUrls = sitemapResult.urls;
      console.log(`Found ${sitemapUrls.length} URLs in sitemap`);
      
      if (sitemapUrls.length === 0) {
        toast.warning('No URLs found in sitemap');
        // Use fallback URLs if no sitemap
        const fallbackUrls = UniversalCrawlerService.generateFallbackUrls(url);
        sitemapUrls.push(...fallbackUrls);
      }

      this.reportProgress('Processing URLs...', 50, `Found ${sitemapUrls.length} URLs in sitemap`, 0, sitemapUrls.length);

      // Create placeholder pages for all sitemap URLs (no content crawling)
      const pages: Page[] = sitemapUrls.map(url => ({
        url,
        content: '', // No content - analysis happens on-demand
        metadata: {
          title: new URL(url).pathname,
          description: ''
        },
        headings: [],
        links: [],
        images: [],
        scripts: [],
        stylesheets: [],
        schema: [],
        wordCount: 0,
        headingStructure: {
          h1: 0, h2: 0, h3: 0, h4: 0, h5: 0, h6: 0
        }
      }));

      this.reportProgress('Completing...', 100, `Found ${sitemapUrls.length} URLs ready for analysis`, sitemapUrls.length, sitemapUrls.length);

      const endTime = Date.now();
      
      // Create sitemap stats - all URLs are ready for analysis
      const allUrls = sitemapUrls.map(url => ({
        url,
        status: 'success' as 'success' | 'failed' | 'pending', // All URLs are available
        statusCode: 200,
        error: undefined
      }));
      
      const sitemapStats = {
        total: sitemapUrls.length,
        successful: sitemapUrls.length, // All URLs are ready
        failed: 0,
        successRate: 100,
        crawlTime: (endTime - startTime) / 1000,
        statusCodes: { '200': sitemapUrls.length },
        failedUrls: [],
        allUrls,
        totalUrls: sitemapUrls.length,
        totalImages: 0,
        urlsWithHreflang: 0,
        totalAltTags: 0,
        totalMissingAltTags: 0,
        averageDepth: 0
      };

      const result: CrawlResult = {
        url,
        pages,
        sitemap: sitemapUrls.length > 0,
        robotsTxt: true, // Assume true since we use Edge Function
        duration: (endTime - startTime) / 1000,
        domain: baseUrl.hostname,
        pagesProcessed: pages.length,
        totalUrls: sitemapUrls.length,
        crawlStartTime: startTime,
        crawlEndTime: endTime,
        sitemapStats
      };

      // Toast notification handled by useCrawlerOperations hook to avoid duplicates
      console.log('Crawl completed:', result);
      
      return result;

    } catch (error) {
      console.error('Crawl failed:', error);
      toast.error(`Crawl failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      throw error;
    }
  }

  private extractTitle(html: string): string {
    const titleMatch = html.match(/<title[^>]*>(.*?)<\/title>/i);
    return titleMatch ? titleMatch[1].trim() : 'Untitled';
  }

  private extractMetaDescription(html: string): string {
    const metaMatch = html.match(/<meta[^>]*name="description"[^>]*content="([^"]*)"[^>]*>/i);
    return metaMatch ? metaMatch[1].trim() : '';
  }

  private extractHeadings(html: string): string[] {
    const headings: string[] = [];
    const headingRegex = /<(h[1-6])[^>]*>(.*?)<\/\1>/gi;
    let match;
    
    while ((match = headingRegex.exec(html)) !== null) {
      const level = match[1].toLowerCase();
      const text = match[2].replace(/<[^>]*>/g, '').trim();
      if (text) {
        headings.push(`${level}:${text}`);
      }
    }
    
    return headings;
  }
}

export const simpleCrawlerService = new SimpleCrawlerService();